import { t } from "@/i18n-js/instance";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { Button } from "@circle-react-shared/uikit/Button";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { Modal } from "@circle-react-uikit/ModalV2";
import type { Appearance } from "../Store/interfaceSlice";
import type { GoogleFontWithWeights } from "../Store/types";
import type { CssVariables, SiteTheme } from "./constants";
import { useSaveCustomSiteTheme } from "./useSaveCustomSiteTheme";
import { createGoogleFontsPayload } from "./utils/fontUtils";

interface CustomThemeConfirmationModalProps {
  appearance: Appearance;
  cssVariables: CssVariables;
  existingThemeId?: number;
  googleFonts: GoogleFontWithWeights[];
  name?: string;
  reset: (values?: { theme: CssVariables }) => void;
  scope?: "site" | "page";
  toggleCurrentCanvasTheme: (theme: SiteTheme) => void;
}

const i18nRootCreateCustomTheme =
  "builder_ui.theme_configuration.custom_theme_confirmation_modal";
const i18nRootUpdateCustomTheme =
  "builder_ui.theme_configuration.update_custom_theme_confirmation_modal";

const CustomThemeConfirmationModalComponent = ({
  appearance,
  cssVariables,
  existingThemeId,
  scope = "page",
  toggleCurrentCanvasTheme,
  reset,
  googleFonts,
  name,
}: CustomThemeConfirmationModalProps) => {
  const modal = useModal();
  const { mutate: saveCustomTheme, isLoading: isSavingCustomTheme } =
    useSaveCustomSiteTheme({ toggleCurrentCanvasTheme, reset });

  const i18nRoot = existingThemeId
    ? i18nRootUpdateCustomTheme
    : i18nRootCreateCustomTheme;

  return (
    <Modal
      isOpen={modal.visible}
      onClose={() => {
        void modal.hide();
      }}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title size="sm" className="font-bold">
            {t([i18nRoot, "title"], {
              theme_name: name || "",
            })}
          </Modal.Title>
          <Modal.HeaderActions>
            <Modal.CloseButton
              onClick={modal.hide}
              disabled={isSavingCustomTheme}
            />
          </Modal.HeaderActions>
        </Modal.Header>
        <Modal.Body className="overflow-y-auto pb-11 md:!px-8">
          <Typography.BodyMd color="text-dark">
            {t([i18nRoot, `${scope}.description`], {
              theme_name: name || "",
            })}
          </Typography.BodyMd>
        </Modal.Body>
        <Modal.Footer className="border-primary flex w-full justify-end gap-4 border-t">
          <Button
            variant="secondary"
            onClick={modal.hide}
            disabled={isSavingCustomTheme}
          >
            {t([i18nRoot, "secondary_cta"])}
          </Button>
          <Button
            variant="circle"
            disabled={isSavingCustomTheme}
            onClick={() => {
              const customFontsPayload = createGoogleFontsPayload(googleFonts);

              saveCustomTheme({
                appearance,
                cssVariables,
                themeScope: scope,
                customFonts: customFontsPayload,
                existingThemeId,
              });
            }}
          >
            {t([i18nRoot, "primary_cta"])}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

const CustomThemeConfirmationModal = ModalManager.create(
  ({
    appearance,
    cssVariables,
    existingThemeId,
    scope,
    toggleCurrentCanvasTheme,
    reset,
    googleFonts,
    name,
  }: CustomThemeConfirmationModalProps) => (
    <CustomThemeConfirmationModalComponent
      appearance={appearance}
      cssVariables={cssVariables}
      existingThemeId={existingThemeId}
      scope={scope}
      toggleCurrentCanvasTheme={toggleCurrentCanvasTheme}
      reset={reset}
      name={name}
      googleFonts={googleFonts}
    />
  ),
);

export const useCustomThemeConfirmationModal = () =>
  useModal(CustomThemeConfirmationModal);
